/* eslint-disable react/prop-types */
import { Document, Page, Text, View, StyleSheet, Image, pdf } from "@react-pdf/renderer"
import moment from "moment/moment"
import JsBarcode from "jsbarcode"
import QRCode from "qrcode"

import * as packageService from "services/packages/package.service"

// Sender printed on the label, per account (Chahin keeps it in the account profile, not reachable by API)
const CHAHIN_SENDERS = {
  account1: { name: "Resuxi Bas Prix", phone: "", mf: "", address: "" },
  account2: { name: "Layouni Commerce", phone: "", mf: "", address: "" },
  account3: { name: "Société Resuxi", phone: "55690494", mf: "1658041/T/N/M/000", address: "Sousse ville" },
  account4: { name: "Bazar Store", phone: "", mf: "", address: "" },
}

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 11 },
  title: { fontSize: 13, fontFamily: "Helvetica-Bold", marginBottom: 6 },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 8,
    borderBottom: "1px solid #FF0000",
  },
  codes: { flexDirection: "row", alignItems: "center" },
  qrcode: { width: 60, height: 60, marginRight: 8 },
  barcode: { width: 190, height: 60 },
  headerInfo: { alignItems: "flex-end" },
  blNumber: { fontSize: 14, fontFamily: "Helvetica-Bold", marginBottom: 4 },
  bold: { fontFamily: "Helvetica-Bold" },
  parties: { flexDirection: "row", justifyContent: "space-between", marginTop: 14, marginBottom: 14 },
  party: { width: "48%", lineHeight: 1.4 },
  table: { border: "1px solid #000" },
  row: { flexDirection: "row", borderBottom: "1px solid #000" },
  cell: { padding: 4, borderRight: "1px solid #000" },
  lastCell: { padding: 4 },
  bottom: { flexDirection: "row" },
  remarkBox: { width: "70%", padding: 6, backgroundColor: "#eeeeee", borderRight: "1px solid #000", lineHeight: 1.4 },
  totals: { width: "30%" },
  totalRow: { flexDirection: "row", borderBottom: "1px solid #000" },
  totalLabel: { width: "50%", padding: 4, borderRight: "1px solid #000" },
  totalValue: { width: "50%", padding: 4 },
  flags: { width: "70%", padding: 6, fontSize: 20, fontFamily: "Helvetica-Bold", borderRight: "1px solid #000" },
})

// Same split as the Chahin label: 81% HT + 19% TVA
const toHT = (price) => ((Number(price) || 0) * 81) / 100

// Generate the CODE128 barcode and the QR code of the EAN in the browser
async function generateChahinCodes(ean) {
  const canvas = document.createElement("canvas")
  JsBarcode(canvas, ean, { format: "CODE128", displayValue: true, fontSize: 14, margin: 4 })
  const qrcode = await QRCode.toDataURL(ean, { margin: 1, width: 200 })
  return { barcode: canvas.toDataURL("image/png"), qrcode }
}

function ChahinLabel({ packageItem, codes, piece, pieces }) {
  const sender = CHAHIN_SENDERS[packageItem.owner] || { name: packageItem.owner || "" }
  const price = Number(packageItem.packagePrice) || 0
  const date = moment(packageItem.createdAt).format("DD/MM/YYYY")
  const isExchange = packageItem.exchange === 1 || packageItem.exchange === true
  return (
    <Page size="A4" style={styles.page}>
      <Text style={styles.title}>Bordereau de livraison</Text>
      <View style={styles.header}>
        <View style={styles.codes}>
          <Image style={styles.qrcode} src={codes.qrcode} />
          <Image style={styles.barcode} src={codes.barcode} />
        </View>
        <View style={styles.headerInfo}>
          <Text style={styles.blNumber}>Bon de livraison N°: {packageItem.reference}</Text>
          <Text style={styles.bold}>Date: {date} - D: 0</Text>
        </View>
      </View>

      <View style={styles.parties}>
        <View style={styles.party}>
          <Text style={styles.bold}>EXPEDITEUR</Text>
          <Text>Nom : {sender.name}</Text>
          <Text>Telephone : {sender.phone}</Text>
          <Text>MF ou CIN : {sender.mf}</Text>
          <Text>Adresse : {sender.address}</Text>
        </View>
        <View style={styles.party}>
          <Text style={styles.bold}>DESTINATAIRE</Text>
          <Text>Nom : {packageItem.clientName}</Text>
          <Text>Telephone : {packageItem.clientPhoneNumber}</Text>
          <Text>
            Adresse : {[packageItem.address, packageItem.city].filter(Boolean).join(", ")} - {packageItem.state?.trim()}
          </Text>
        </View>
      </View>

      <View style={styles.table}>
        <View style={styles.row}>
          <Text style={{ ...styles.cell, width: "15%" }}>Référence</Text>
          <Text style={{ ...styles.cell, width: "40%" }}>Designation</Text>
          <Text style={{ ...styles.cell, width: "15%" }}>P.U</Text>
          <Text style={{ ...styles.cell, width: "10%" }}>Qte</Text>
          <Text style={{ ...styles.lastCell, width: "20%" }}>Total.U</Text>
        </View>
        <View style={styles.row}>
          <Text style={{ ...styles.cell, width: "15%" }}>{packageItem.reference}</Text>
          <Text style={{ ...styles.cell, width: "40%" }}>{packageItem.description}</Text>
          <Text style={{ ...styles.cell, width: "15%" }}>{toHT(price).toFixed(2)}</Text>
          <Text style={{ ...styles.cell, width: "10%" }}>1</Text>
          <Text style={{ ...styles.lastCell, width: "20%" }}>{toHT(price).toFixed(2)}</Text>
        </View>
        <View style={{ ...styles.bottom, borderBottom: "1px solid #000" }}>
          <View style={styles.remarkBox}>
            {packageItem.comments ? <Text>{packageItem.comments}</Text> : null}
            <Text>
              Nombre: {piece}/{pieces}
            </Text>
          </View>
          <View style={styles.totals}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total HT</Text>
              <Text style={styles.totalValue}>{toHT(price).toFixed(2)}</Text>
            </View>
            <View style={{ flexDirection: "row", flexGrow: 1 }}>
              <Text style={styles.totalLabel}>TVA</Text>
              <Text style={styles.totalValue}>19%</Text>
            </View>
          </View>
        </View>
        <View style={styles.bottom}>
          <Text style={styles.flags}>{isExchange ? "ECHANGE" : " "}</Text>
          <View style={{ ...styles.totals, flexDirection: "row" }}>
            <Text style={{ ...styles.totalLabel, paddingTop: 10 }}>Total</Text>
            <Text style={{ ...styles.totalValue, fontSize: 15, fontFamily: "Helvetica-Bold" }}>{price}</Text>
          </View>
        </View>
      </View>
    </Page>
  )
}

// One page per piece, like the Chahin labels
export function ChahinPackageDocument({ labels }) {
  return (
    <Document title="Chahin Colis" language="fr" subject="Chahin bordereau de livraison">
      {labels.map(({ packageItem, codes }) => {
        const pieces = Number(packageItem.productQuantity) || 1
        return Array.from({ length: pieces }, (_, index) => (
          <ChahinLabel
            key={`${packageItem.barcode}-${index}`}
            packageItem={packageItem}
            codes={codes}
            piece={index + 1}
            pieces={pieces}
          />
        ))
      })}
    </Document>
  )
}

// Flag the packages as printed (Chahin + local) and build their labels.
// Returns the PDF url and the warnings to display.
export async function printChahinPackages(packageList = []) {
  const labels = []
  const warnings = []
  for (const packageItem of packageList) {
    const { status, data } = await packageService.printChahinPackage(packageItem.packageId)
    if (status !== 200) throw new Error(data?.message || "Impossible d'imprimer le colis Chahin")
    if (data?.warning) warnings.push(data.warning)
    labels.push({ packageItem, codes: await generateChahinCodes(packageItem.barcode) })
  }
  const blob = await pdf(<ChahinPackageDocument labels={labels} />).toBlob()
  return { url: URL.createObjectURL(blob), warnings }
}
